<template>
  <v-container fluid>
    <v-dialog v-model="visible" persistent max-width="80vw" @input="backToParent($event)">
      <v-card>
        <v-card-title primary-title class="justify-center">
          {{ updateMode ? $t('credentials.title.update') : $t('credentials.title.create') }}
          <v-spacer></v-spacer>
        </v-card-title>

        <v-list-item>
          <v-list-item-content>
            <v-form id="credentialForm" ref="credentialForm" v-model="credentialFormValid" if="credential_form">
              <v-row v-if="showUserSelectCombo">
                <v-col cols="6">
                  <v-autocomplete
                    id="selected_user"
                    v-model="selectedUser"
                    :items="enabledUsers"
                    item-text="email"
                    return-object
                    :label="$t('credentials.content.userName') + `*`"
                    :placeholder="$t('credentials.content.startTyping')"
                    :rules="[rules.required]"
                    prepend-inner-icon="mdi-account"
                    :no-data-text="noDatatext"
                    clearable
                    outlined
                    hide-details
                    dense
                    :search-input="search"
                    @update:search-input="listEnabledUsersWacther"
                  >
                    <template slot="item" slot-scope="{ item }">
                      <span class="body-2">{{ item.name }}  <span v-if="!!item.email" class=" caption">&lt;{{ item.email }}&gt; </span></span>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="9">
                  <v-text-field
                    id="description"
                    v-model="credential.description"
                    :label="$t('credentials.content.description') + `*`"
                    :counter="maxCharacters"
                    :rules="[rules.required, rules.minMax]"
                  ></v-text-field>
                </v-col>
                <v-col cols="3">
                  <v-select
                    id="keypurpose"
                    v-model="keypurposeSelected"
                    :items="keyPurposeList"
                    :label="$t('credentials.content.keyPurposeLabel') + `*`"
                    chips
                    :disabled="updateMode"
                  ></v-select>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3">
                  <v-select
                    id="key_alg"
                    v-model="keyAlgorithm"
                    :items="keyAlgorithmList"
                    :label="$t('credentials.content.keyAlgorithmLabel') + `*`"
                    chips
                    :disabled="updateMode"
                    :rules="[rules.required]"
                    @change="changeDefaultValuesByKeyAlg"
                  ></v-select>
                </v-col>
                <v-col cols="3">
                  <v-select
                    id="key_size"
                    v-model="keySizeOrName"
                    :items="keyAlgorithm === keyAlgorithmList[1] ? ecCurveNameList : rsaKeySizeList"
                    :label="$t('credentials.content.keySizeOrCurveNameLabel') + `*`"
                    chips
                    :disabled="updateMode"
                    :rules="[rules.required]"
                  ></v-select>
                </v-col>
                <v-col cols="3">
                  <v-select
                    id="auth_mode"
                    v-model="credential.authMode"
                    :items="authModeList"
                    :label="$t('credentials.content.authModeLabel') + `*`"
                    chips
                    :disabled="updateMode"
                    :rules="[rules.required]"
                  ></v-select>
                </v-col>
                <v-col cols="3">
                  <v-select
                    v-if="!updateMode"
                    id="cert_enroll_mode"
                    v-model="credential.certificateEnrollmentMode"
                    :items="certificateEnrollmentModeList"
                    :label="$t('credentials.content.certificateEnrollmentModeLabel') + `*`"
                    chips
                    :rules="[rules.required]"
                  ></v-select>
                </v-col>
              </v-row>
              <div v-if="!updateMode">
                <v-row align="center">
                  <v-col cols="6">
                    <v-text-field
                      v-if="credential.certificateEnrollmentMode === 'CMP'"
                      id="common_name"
                      v-model="distinguishedName.commonName"
                      :label="$t('x509CertificateAttributes.distinguishedName.commonName') + `*`"
                      :rules="[rules.required, rules.max64]"
                      :counter="commonNameMaxCharacters"
                    ></v-text-field>
                    <v-text-field
                      v-if="credential.certificateEnrollmentMode === 'MANUAL'"
                      id="common_name"
                      v-model="distinguishedName.commonName"
                      :rules="[rules.required, rules.max64]"
                      :label="$t('x509CertificateAttributes.distinguishedName.commonName') + `*`"
                      :counter="commonNameMaxCharacters"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-text-field
                      id="surname"
                      v-model="distinguishedName.surname"
                      :label="$t('x509CertificateAttributes.distinguishedName.surname')"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-text-field
                      id="given_name"
                      v-model="distinguishedName.givenName"
                      :label="$t('x509CertificateAttributes.distinguishedName.givenName')"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row align="center">
                  <v-col cols="6">
                    <v-text-field
                      id="organization"
                      v-model="distinguishedName.organization"
                      :label="$t('x509CertificateAttributes.distinguishedName.organizationName')"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      id="organizational_unit"
                      v-model="distinguishedName.organizationalUnit"
                      :label="$t('x509CertificateAttributes.distinguishedName.organizationUnitName')"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row align="center">
                  <v-col cols="2">
                    <v-select
                      id="country_name"
                      v-model="distinguishedName.countryName"
                      :label="$t('x509CertificateAttributes.distinguishedName.country')"
                      :items="countries"
                      item-text="text"
                      item-value="value"
                      prepend-icon="mdi-map"
                      single-line
                    ></v-select>
                  </v-col>
                  <v-col cols="4">
                    <v-text-field
                      id="locality_name"
                      v-model="distinguishedName.localityName"
                      :label="$t('x509CertificateAttributes.distinguishedName.localityName')"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      id="email_address"
                      v-model="distinguishedName.emailAddress"
                      :label="$t('x509CertificateAttributes.distinguishedName.emailAddress')"
                      :rules="[rules.email]"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row align="center">
                  <v-col cols="2">
                    <v-text-field
                      id="serial_number"
                      v-model="distinguishedName.serialNumber"
                      :label="$t('x509CertificateAttributes.distinguishedName.serialNumber')"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="4">
                    <v-text-field
                      id="title"
                      v-model="distinguishedName.title"
                      :label="$t('x509CertificateAttributes.distinguishedName.title')"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      id="state_name"
                      v-model="distinguishedName.stateOrProvinceName"
                      :label="$t('x509CertificateAttributes.distinguishedName.stateOrProvinceName')"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </div>
            </v-form>
            <!-- OIDS -->
            <v-form v-if="!updateMode" ref="addFieldForm" v-model="addFieldFormValid" @submit.prevent="addField">
              <v-row align="center">
                <fieldset class="ml-3 pa-3">
                  <legend>{{ $t('x509CertificateAttributes.oids.addNewOIDFields') }}</legend>
                  <v-container class="pa-3">
                    <v-row class="d-flex align-center justify-left">
                      <v-col cols="4">
                        <v-text-field v-model="oid" :rules="[oidrules.oidCheck]" label="OID"></v-text-field>
                      </v-col>
                      <v-col cols="4">
                        <v-text-field v-model="oidvalue" :rules="[oidrules.oidValueCheck]" :required="!!oid" :label="$t('x509CertificateAttributes.oids.value')"></v-text-field>
                      </v-col>
                      <v-col cols="4" class="align-self-center">
                        <v-btn class="modalButton" :disabled="isAddButtonDisabled" @click="addField">
                          {{ oidModifyMode ? $t('x509CertificateAttributes.oids.modifyField') : $t('x509CertificateAttributes.oids.addField') }}
                        </v-btn>
                      </v-col>
                      <v-data-table
                        v-if="distinguishedName.customFields.length > 0"
                        :headers="oidTableHeaders"
                        :items="distinguishedName.customFields"
                        hide-default-footer
                        class="elevation-1 ma-3"
                      >
                        <template #[`item.action`]="{ item }">
                          <v-btn icon @click="updateOidField(item)">
                            <v-icon>
                              mdi-pencil-outline
                            </v-icon>
                          </v-btn>
                          <v-btn color="error" icon @click="deleteOidField(item)">
                            <v-icon>
                              mdi-close-circle-outline
                            </v-icon>
                          </v-btn>
                        </template>
                      </v-data-table>
                    </v-row>
                  </v-container>
                </fieldset>
              </v-row>
            </v-form>
          </v-list-item-content>
        </v-list-item>
        <v-card-actions>
          <v-btn id="cancel_button" @click="back()">{{ $t('main.cancel') }}</v-btn>
          <v-btn
            id="create_button"
            color="primary"
            :loading="btnLoading"
            :disabled="!credentialFormValid"
            @click="createOrUpdateCredential"
          >
            {{ updateMode ? $t('main.update') : $t('main.create') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import debounce from 'lodash.debounce'
import { mapGetters } from 'vuex'
import Role from '../../Role'
import { showToastError, showToastSuccess } from '../../event-bus'
import credentials from '../../services/api/credentials'
import server from '../../services/api/server'
import users from '../../services/api/users'
import AuthMode from './AuthMode'
import CertificateEnrollmentMode from './CertificateEnrollmentMode'
import EcCurveName from './EcCurveName'
import KeyAlgorithm from './KeyAlgorithm'
import KeyPurpose from './KeyPurpose'
import RsaKeySize from './RsaKeySize'

export default {
  name: 'CreateOrUpdateCredential',

  data() {
    return {
      visible: true,
      credentialFormValid: false,
      addFieldFormValid: false,
      showUserSelectCombo: false,
      pagination: {
        sortBy: ['name']
      },
      search: '',
      enabledUsers: [],
      selectedUser: null,
      minCharacters: 4,
      maxCharacters: 255,
      commonNameMaxCharacters: 64,
      countryMinCharacters: 2,
      countryMaxCharacters: 2,
      btnLoading: false,
      credential: {
        authMode: Object.values(AuthMode)[1],
        certificateEnrollmentMode: Object.keys(CertificateEnrollmentMode)[0],
        keyPurpose: Object.keys(KeyPurpose)[0]
      },
      distinguishedName: {
        customFields: []
      },
      countries: [],
      oid: '',
      oidvalue: '',
      oidTableHeaders: [
        {
          text: 'OID',
          value: 'oid'
        },
        {
          text: this.$t('x509CertificateAttributes.oids.value'),
          value: 'value'
        },
        {
          text: this.$t('dataTable.header.actions'),
          value: 'action',
          sortable: false,
          width: 'auto',
          align: 'right'

        }
      ],
      keyAlgorithmList: KeyAlgorithm,
      rsaKeySizeList: RsaKeySize,
      ecCurveNameList: EcCurveName,
      authModeList: Object.values(AuthMode),
      certificateEnrollmentModeList: Object.keys(CertificateEnrollmentMode).map(key => ({
        text: this.$t(`credentials.content.certificateEnrollmentModeLabels.${key}`),
        value: key
      })),
      keyAlgorithm: null,
      keySizeOrName: null,
      /* keyPurposeList: Object.keys(KeyPurpose).map(key => ({
        text: this.$t(`credentials.content.keyPurposeLabels.${key}`),
        value: key
      })), */
      keyPurposeList: Object.keys(KeyPurpose)
        .filter(key => key !== 'SYMMETRIC_ENCRYPT') // Temporary exclude SYMMETRIC_ENCRYPT
        .map(key => ({
          text: this.$t(`credentials.content.keyPurposeLabels.${key}`),
          value: key
        })),
      keypurposeSelected: Object.keys(KeyPurpose)[0],
      user: {},
      noDatatext: this.$t('credentials.content.startTyping'),
      isAddButtonDisabled: true,
      rules: {
        required: (v) => !!v || !isNaN(parseFloat(v)) || this.$t('validation.form.requiredField'),
        minMax: (v) => ((v && v.length >= this.minCharacters) && (v && v.length <= this.maxCharacters)) || this.$t('validation.form.mustBeBetween', { min: this.minCharacters, max: this.maxCharacters }),
        countryMax: (v) => ((!v) || (v.length === this.countryMaxCharacters)) || this.$t('validation.form.mustBe', { value: this.countryMaxCharacters }),
        email: v => !v || /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || this.$t('validation.form.emailMustBeValid'),
        max64: v => !v || (v.length <= this.commonNameMaxCharacters)
      },
      oidrules: {
        oidCheck: v => !v || /^([0-2])((\.0)|(\.[1-9][0-9]*))*$/.test(v) || this.$t('validation.form.oidMustBeValid'),
        oidValueCheck: (v) => (this.oid && this.oid.length > 0) ? !!v || this.$t('validation.form.requiredField') : true
      },
      oidModifyMode: false
    }
  },

  computed: {
    ...mapGetters(['hasRole']),
    isAdmin() {
      return this.hasRole(Role.ADMIN)
    },
    updateMode() {
      return this.$route.params.credentialId != null
    },
    currentUser() {
      return this.$store.state.currentUser
    },
    isAddButtonDisabledComputed() {
      const state = !!this.$refs.addFieldForm || (this.$refs.addFieldForm === undefined) || !this.$refs.addFieldForm.validate() || (!!(this.oid && this.oid.length >= 0) && (this.oidvalue && this.oidvalue.length > 0))
      return state
    }
  },

  watch: {
    '$refs.addFieldForm'(newVal) {
      this.validateAddButton()
    },
    oid(newVal) {
      this.validateAddButton()
    },
    oidvalue(newVal) {
      this.validateAddButton()
    }
  },

  async mounted() {
    if (this.updateMode) {
      this.getCredential()
    }
    await this.getMyUser()

    if ((this.$route.params.userId === undefined) && this.isAdmin) {
      await this.listEnabledUsers()
      this.showUserSelectCombo = true
    }

    const entries = await server.getCountryList(this.user.locale || 'en')
    this.countries = entries.flatMap(e => {
      return { text: e.name, value: e.code }
    })
  },

  methods: {
    validateAddButton() {
      const formValid = !!this.$refs.addFieldForm && this.$refs.addFieldForm.validate()
      const fieldValid = (this.oid && this.oid.length > 0 && (this.oidvalue && this.oidvalue.length > 0))
      this.isAddButtonDisabled = !formValid || !fieldValid
    },

    listEnabledUsersWacther: debounce(function(search) {
      this.listEnabledUsers(search)
    }, 500),

    async listEnabledUsers(searchUser) {
      const pageReq = {
        maxResults: 5
      }

      let searchUserlen = 0
      try {
        searchUserlen = searchUser.length
      } catch (err) {}

      if (searchUserlen > 0) {
        try {
          const resp = await users.listUsers(searchUser, pageReq)
          this.enabledUsers = resp.content
          if (resp.content.length === 0) this.noDatatext = this.$t('credentials.content.userNotFound')
        } catch (error) {
          const errorMsg = this.getErrorMessage(error)
          showToastError(errorMsg)
        }
      } else {
        this.noDatatext = this.$t('credentials.content.startTyping')
      }
    },
    async createOrUpdateCredential() {
      this.btnLoading = true
      if (!this.$refs.credentialForm.validate()) {
        return
      }

      try {
        if (this.isAdmin) {
          this.$set(this.credential, 'userId', this.$route.params.userId ? this.$route.params.userId : this.selectedUser.id)
        }

        this.credential.keyAlg = this.keyAlgorithm.toLowerCase() // keyAlg must be lowerCase -> server only eats it that way
        this.$set(this.credential, this.keyAlgorithm.includes(this.keyAlgorithmList[0]) ? 'rsaKeySize' : 'ecCurveName', this.keySizeOrName)
        this.setKeyPurpose()

        if (this.updateMode) {
          await credentials.updateCredential(this.$route.params.credentialId, this.credential)
        } else {
          if (JSON.stringify(this.distinguishedName) !== JSON.stringify({})) {
            this.$set(this.credential, 'distinguishedName', Object.assign({}, this.distinguishedName))
          }
          await credentials.createCredential(this.credential)
        }

        showToastSuccess(this.updateMode ? this.$t('toastMessage.succesfullyUpdated') : this.$t('toastMessage.succesfullyCreated'))
        this.back()
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      } finally {
        this.btnLoading = false
      }
    },

    async getCredential() {
      try {
        this.credential = await credentials.getCredential(this.$route.params.credentialId)
        this.setBasicValuesByGetCredential()
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    async getMyUser() {
      try {
        this.user = await users.getMyUser()
        if (this.user.distinguishedName) {
          this.distinguishedName = this.user.distinguishedName
        }
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    changeDefaultValuesByKeyAlg() {
      this.keySizeOrName = this.keyAlgorithm === this.keyAlgorithmList[1] ? this.ecCurveNameList[0] : this.rsaKeySizeList[0]
    },

    setBasicValuesByGetCredential() {
      this.keyAlgorithm = this.credential.keyAlg.toUpperCase() // due to display
      this.keySizeOrName = this.keyAlgorithm === this.keyAlgorithmList[0] ? this.credential.rsaKeySize : this.credential.ecCurveName
      this.keypurposeSelected = this.credential.keyPurpose
    },

    setKeyPurpose() {
      this.credential.keyPurpose = this.keypurposeSelected
    },

    back() {
      this.$router.push({ name: 'Credentials' })
    },

    backToParent(e) {
      if (!e) {
        this.$router.go(-1)
      }
    },

    addField(modifiedField) {
      if ((this.oid.length === 0) || (this.oidvalue.length === 0)) {
        return
      }

      if (this.oidModifyMode && modifiedField) {
        this.oidModifyMode = false
        this.distinguishedName.customFields.pop(modifiedField)
      }

      this.distinguishedName.customFields.push({
        oid: this.oid,
        value: this.oidvalue
      })

      this.oid = ''
      this.oidvalue = ''
      this.$refs.addFieldForm.resetValidation()
    },

    deleteOidField(item) {
      const index = this.distinguishedName.customFields.indexOf(item)
      if (index > -1) {
        this.distinguishedName.customFields.splice(index, 1)
      }
    },
    updateOidField(oidField) {
      this.oid = oidField.oid
      this.oidvalue = oidField.value
      this.oidModifyMode = true
    }

  }
}
</script>

<style>
</style>
